import React, { useCallback } from 'react'
import clsx from 'clsx'
import { Button } from '@instacart/tds'

import styles from './ActionFlow.module.scss'
import { useActionFlow } from './ActionFlowContext'
import type { ActionFlowAction, RunActionOptions } from './types'

type TdsButtonProps = React.ComponentProps<typeof Button>

export type ActionFlowButtonProps = Omit<TdsButtonProps, 'onClick' | 'disabled' | 'loading'> & {
  action: ActionFlowAction
  options?: RunActionOptions
  disabled?: boolean
}

export const ActionFlowButton: React.FC<ActionFlowButtonProps> = ({
  action,
  options,
  disabled,
  className,
  children,
  fullWidth,
  ...props
}) => {
  const { runAction, isActionInProgress, isPending, activeLayout, modalLayout } = useActionFlow()
  const effectiveLayout = activeLayout ?? modalLayout
  const isInlineRight = effectiveLayout === 'inlineRight'

  const isBusy = isActionInProgress || isPending
  const computedDisabled = !!disabled || isBusy

  const onClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      if (computedDisabled) return
      void runAction(action, options, e)
    },
    [action, computedDisabled, options, runAction]
  )

  return (
    <Button
      {...props}
      className={clsx(styles.button, isInlineRight && styles.buttonInline, className)}
      disabled={computedDisabled}
      loading={isBusy}
      onClick={onClick}
      aria-busy={isBusy || undefined}
      fullWidth={fullWidth ?? !isInlineRight}
    >
      {children}
    </Button>
  )
}
